import {
  FormControl,
  InputLabel,
  OutlinedInput,
  useMediaQuery,
  Snackbar,
  Alert,
} from "@mui/material";
import "./Permissions.scss";
import { AdminPermissions } from "../../SourceData";
import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import api from "../../utils/api";
import BlueButton from "../../components/BlueButton/BlueButton";

const Permissions = ({ onSuccess = () => {} }) => {
  const { id } = useParams();
  const isMobile = useMediaQuery("(max-width:767px)");
  const isTablet = useMediaQuery("(min-width: 768px) and (max-width: 1024px)");

  const [credentialName, setCredentialName] = useState("");
  const [selectedPermissions, setSelectedPermissions] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [alertInfo, setAlertInfo] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const isEditMode = !!id;

  // Helper function to check if all permissions in a category are selected
  const areAllPermissionsSelected = (category) => {
    return category.subPermissions.every(
      (perm) => selectedPermissions[perm.id]
    );
  };

  // Helper function to check if a permission was auto-selected
  const isAutoSelected = (permissionId) => {
    let currentCategory = null;
    let currentPermissionIndex = -1;

    AdminPermissions.forEach((category) => {
      const permIndex = category.subPermissions.findIndex(
        (p) => p.id === permissionId
      );
      if (permIndex !== -1) {
        currentCategory = category;
        currentPermissionIndex = permIndex;
      }
    });

    if (!currentCategory || currentPermissionIndex !== 0) {
      return false;
    }

    const firstPermissionId = currentCategory.subPermissions[0]?.id;
    const hasSubPermissions = currentCategory.subPermissions
      .slice(1)
      .some((p) => selectedPermissions[p.id]);

    return selectedPermissions[firstPermissionId] && hasSubPermissions;
  };

  // Fetch credential data for edit mode
  const fetchCredentialData = async (credentialId) => {
    try {
      setIsLoading(true);
      const response = await api.get(`/credentials/${credentialId}`);

      if (response.data && response.data.status === "success") {
        const credentialData = response.data.data;

        setCredentialName(credentialData.name || "");

        const permissionsObj = {};
        if (
          credentialData.permissions &&
          Array.isArray(credentialData.permissions)
        ) {
          credentialData.permissions.forEach((permissionId) => {
            permissionsObj[permissionId] = true;
          });
        }
        setSelectedPermissions(permissionsObj);
      }
    } catch (error) {
      console.error("Error fetching credential data:", error);
      setAlertInfo({
        open: true,
        message: "حدث خطأ أثناء تحميل بيانات الصلاحية",
        severity: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isEditMode && id) {
      fetchCredentialData(id);
    }
  }, [id, isEditMode]);

  // Handle select all for a category
  const handleSelectAllCategory = (category, isChecked) => {
    setSelectedPermissions((prev) => {
      const updated = { ...prev };

      if (isChecked) {
        // Select all permissions in the category
        category.subPermissions.forEach((perm) => {
          updated[perm.id] = true;
        });
      } else {
        // Deselect all permissions in the category
        category.subPermissions.forEach((perm) => {
          delete updated[perm.id];
        });
      }

      return updated;
    });
  };

  const handleCheckboxChange = (permissionId, isChecked) => {
    setSelectedPermissions((prev) => {
      const updated = { ...prev };

      let currentCategory = null;
      let currentPermissionIndex = -1;

      AdminPermissions.forEach((category) => {
        const permIndex = category.subPermissions.findIndex(
          (p) => p.id === permissionId
        );
        if (permIndex !== -1) {
          currentCategory = category;
          currentPermissionIndex = permIndex;
        }
      });

      if (!currentCategory) {
        if (isChecked) {
          updated[permissionId] = true;
        } else {
          delete updated[permissionId];
        }
        return updated;
      }

      const firstPermissionId = currentCategory.subPermissions[0]?.id;
      const isFirstPermission = currentPermissionIndex === 0;

      if (isChecked) {
        updated[permissionId] = true;

        if (!isFirstPermission && firstPermissionId) {
          updated[firstPermissionId] = true;
        }
      } else {
        delete updated[permissionId];

        if (isFirstPermission) {
          currentCategory.subPermissions.forEach((subPerm) => {
            if (subPerm.id !== firstPermissionId) {
              delete updated[subPerm.id];
            }
          });
        }
      }

      return updated;
    });
  };

  const handleCloseAlert = () => {
    setAlertInfo((prev) => ({ ...prev, open: false }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!credentialName.trim()) {
      setAlertInfo({
        open: true,
        message: "الرجاء إدخال اسم الصلاحية",
        severity: "error",
      });
      return;
    }

    const selectedPermissionIds = Object.keys(selectedPermissions)
      .filter((permId) => selectedPermissions[permId])
      .map((permId) => parseInt(permId));

    if (selectedPermissionIds.length === 0) {
      setAlertInfo({
        open: true,
        message: "الرجاء اختيار صلاحية واحدة على الأقل",
        severity: "error",
      });
      return;
    }

    const data = {
      name: credentialName.trim(),
      permissions: selectedPermissionIds,
    };

    try {
      setIsSubmitting(true);

      let response;
      if (isEditMode) {
        response = await api.patch(`/credentials/${id}`, data);
      } else {
        response = await api.post("/credentials", data);
      }

      setAlertInfo({
        open: true,
        message: isEditMode
          ? "تم تحديث الصلاحية بنجاح"
          : "تم إضافة الصلاحية بنجاح",
        severity: "success",
      });

      if (!isEditMode) {
        setCredentialName("");
        setSelectedPermissions({});
      }

      onSuccess(response.data);
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        (isEditMode
          ? "حدث خطأ أثناء تحديث الصلاحية"
          : "حدث خطأ أثناء إضافة الصلاحية");

      setAlertInfo({
        open: true,
        message: errorMessage,
        severity: "error",
      });
      console.error("Error with credential:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isEditMode && isLoading) {
    return (
      <div
        className="permissions"
        style={{ textAlign: "center", padding: "50px" }}
      >
        <p>جاري تحميل بيانات الصلاحية...</p>
      </div>
    );
  }

  return (
    <div className="permissions">
      <Snackbar
        open={alertInfo.open}
        autoHideDuration={6000}
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleCloseAlert} severity={alertInfo.severity}>
          {alertInfo.message}
        </Alert>
      </Snackbar>

      <form onSubmit={handleSubmit} noValidate autoComplete="off">
        <div className="permissions-header">
          <p className="title">
            {isEditMode ? "تعديل الصلاحية" : "اضافة صلاحية جديدة"}
          </p>
          <FormControl
            sx={{ width: isMobile ? "100%" : isTablet ? "50%" : "900px" }}
            variant="outlined"
          >
            <InputLabel
              htmlFor="credential-name-input"
              shrink
              sx={{
                background: "white",
                transformOrigin: "right top",
                transform: "translate(14px, -9px) scale(0.75)",
                right: 24,
                left: "auto",
                paddingLeft: "4px",
                paddingRight: "4px",
              }}
            >
              اسم الصلاحية
            </InputLabel>
            <OutlinedInput
              sx={{ borderRadius: "8px" }}
              required
              id="credential-name-input"
              type="text"
              notched
              value={credentialName}
              onChange={(e) => setCredentialName(e.target.value)}
              disabled={isLoading}
            />
          </FormControl>
        </div>

        <div className="check-group">
          {AdminPermissions?.map((category) => (
            <div
              className="check"
              key={category.id}
              style={{
                width: isMobile ? "100%" : "32%",
              }}
            >
              <div className="header">
                
                {category?.name}
                <div className="select-all-item" style={{ marginTop: "8px" }}>
                  <input
                    type="checkbox"
                    id={`select-all-${category.id}`}
                    checked={areAllPermissionsSelected(category)}
                    onChange={(e) =>
                      handleSelectAllCategory(category, e.target.checked)
                    }
                    disabled={isLoading}
                  />
                  <label
                    htmlFor={`select-all-${category.id}`}
                    style={{
                      fontWeight: "bold",
                      fontSize: "0.9em",
                    }}
                  >
                    تحديد الكل
                  </label>
                </div>
              </div>
              <div className="body">
                {category?.subPermissions?.map((subPermission, index) => (
                  <div className="item" key={subPermission.id}>
                    <input
                      type="checkbox"
                      id={`permission-${subPermission.id}`}
                      checked={!!selectedPermissions[subPermission.id]}
                      onChange={(e) =>
                        handleCheckboxChange(subPermission.id, e.target.checked)
                      }
                      disabled={isLoading}
                    />
                    <label
                      htmlFor={`permission-${subPermission.id}`}
                      style={{
                        opacity: isAutoSelected(subPermission.id) ? 0.7 : 1,
                        fontStyle: isAutoSelected(subPermission.id)
                          ? "italic"
                          : "normal",
                      }}
                    >
                      {subPermission?.name}
                      {isAutoSelected(subPermission.id) && (
                        <span
                          style={{
                            fontSize: "0.8em",
                            color: "#666",
                            marginLeft: "5px",
                          }}
                        >
                          (تم التحديد تلقائياً)
                        </span>
                      )}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div
          className="submit-container"
          style={{ textAlign: "center", margin: "20px 0" }}
        >
          <BlueButton
            text={
              isSubmitting
                ? isEditMode
                  ? "جاري التحديث..."
                  : "جاري الإضافة..."
                : isEditMode
                ? "تحديث الصلاحية"
                : "إضافة صلاحية"
            }
            size="large"
            variant="fill"
            type="submit"
            disabled={isSubmitting || isLoading}
          />
        </div>
      </form>
    </div>
  );
};

export default Permissions;